import { AgentRunDto } from "@growth/dto";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronUp, CircleCheck, TriangleAlert } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { useActive } from "../lib/active";
import { runLink, useActiveRuns } from "../lib/agent-runs";
import { api } from "../lib/api";
import { AgentActivity, LiveDot } from "./agent-activity";

/** How long a "finished" note stays in the dock. */
const NOTICE_MS = 9000;

/**
 * Agents at work, on every page: a pill in the corner saying how many are working and what the
 * newest one is doing right now, opening into each run's live steps. When a run finishes, the
 * dock says so for a few seconds, then gets out of the way.
 */
export const AgentsDock = () => {
  const { workspace, brand } = useActive();
  const queryClient = useQueryClient();
  const runs = useActiveRuns(Boolean(workspace));
  const active = runs.data ?? [];
  const [open, setOpen] = useState(false);
  const [finished, setFinished] = useState<AgentRunDto[]>([]);
  const previous = useRef<Set<string>>(new Set());

  // A run that drops off the active list has finished: fetch how it ended and say so.
  useEffect(() => {
    if (!runs.data) return;
    const now = new Set(runs.data.map((run) => run.id));
    const gone = [...previous.current].filter((id) => !now.has(id));
    previous.current = now;
    for (const id of gone) {
      void api
        .get<AgentRunDto>(`/agent-runs/${id}`)
        .then(({ data }) => {
          setFinished((current) => [data, ...current.filter((run) => run.id !== id)].slice(0, 3));
          // Pages showing this run's work refresh themselves.
          void queryClient.invalidateQueries({ queryKey: ["agent-runs", id] });
          window.setTimeout(
            () => setFinished((current) => current.filter((run) => run.id !== id)),
            NOTICE_MS,
          );
        })
        .catch(() => undefined);
    }
  }, [runs.data, queryClient]);

  if (!active.length && !finished.length) return null;
  const newest = active[0];
  const latest = newest?.activity.at(-1)?.text;

  return (
    <div className="fixed bottom-5 right-5 z-40 flex w-[min(400px,calc(100vw-2.5rem))] flex-col items-end gap-2">
      {open && active.length > 0 && (
        <div
          id="agents-dock-panel"
          className="max-h-[65vh] w-full space-y-3 overflow-y-auto rounded-2xl border border-line bg-paper p-3 shadow-2xl"
        >
          {active.map((run) => {
            const link = runLink(run);
            const here = !run.brandId || run.brandId === brand?.id;
            return (
              <div key={run.id} className="space-y-1">
                <AgentActivity run={run} limit={4} />
                {link && here && (
                  <Link
                    to={link}
                    onClick={() => setOpen(false)}
                    className="block px-1 text-right text-xs text-accent underline underline-offset-2"
                  >
                    Open where this lands
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      )}

      {finished.map((run) => (
        <div
          key={run.id}
          role="status"
          className="flex w-full items-start gap-2.5 rounded-xl border border-line bg-white px-4 py-3 text-sm shadow-lg"
        >
          {run.status === "succeeded" ? (
            <CircleCheck className="mt-0.5 size-4 shrink-0 text-emerald-700" aria-hidden="true" />
          ) : (
            <TriangleAlert className="mt-0.5 size-4 shrink-0 text-amber-600" aria-hidden="true" />
          )}
          <span className="min-w-0">
            <span className="font-medium text-ink">
              {run.agentName} {run.status === "succeeded" ? "finished" : "stopped"}
            </span>
            <span className="block truncate text-muted">
              {run.activity.at(-1)?.text ?? run.error ?? run.title}
            </span>
          </span>
        </div>
      ))}

      {active.length > 0 && (
        <button
          type="button"
          aria-expanded={open}
          aria-controls="agents-dock-panel"
          onClick={() => setOpen(!open)}
          className="flex max-w-full items-center gap-2.5 rounded-full bg-ink py-2.5 pl-4 pr-3 text-sm text-white shadow-xl transition-transform active:scale-[0.97]"
        >
          <LiveDot />
          <span className="shrink-0 font-medium">
            {active.length === 1
              ? `${newest?.agentName} is working`
              : `${active.length} agents working`}
          </span>
          {latest && <span className="min-w-0 truncate text-white/70">{latest}</span>}
          {open ? (
            <ChevronDown className="size-4 shrink-0" aria-hidden="true" />
          ) : (
            <ChevronUp className="size-4 shrink-0" aria-hidden="true" />
          )}
        </button>
      )}
    </div>
  );
};
